// The page behind the emailed link to finish an unfinished consult (CR-015,
// Wes Oct 18 2026): "<origin>/resume#<token>". Without a token it is the
// same-browser offer from the check-in page (CR-016): the consult cookie
// proves it, server side. The token is in the fragment,
// so it never reaches a server log; it travels only in POST bodies. The page
// greets the owner by first name, asks for consent to the notice in force
// now, and starts the consult that continues the earlier one: Otto picks up
// where they left off. Nothing about the earlier call is shown on screen.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCheckinConfig, getResumable, postResume, postResumeHere, postResumeInfo, type CheckinConfig } from "./api";
import { primeCallAudio, releasePrimedCallAudio } from "./call/audio/audioPrime";
import { releaseMicrophone, requestMicrophone } from "./call/audio/micPermission";
import { micProblemText } from "./checkin/MicCheck";
import { ConsultBanner, PublicFooter, PublicHeader } from "./checkin/PublicChrome";
import { saveConsultRecord } from "./session";
import { stringsFor } from "./strings";
import { stripDashes } from "./visibleText";
import "./consult.css";

// The page's own words (the Spanish wants a native speaker's look, like the rest of the Spanish).
const WORDS = {
  en: {
    title: (name: string) => `Welcome back, ${name}.`,
    nothing: "There's no consult to pick up in this browser. You can start a new one any time.",
    body: (business: string) => `Otto kept your place. Pick up the consult about ${business} where you left off; it takes a few more minutes.`,
    start: "Pick up where we left off",
    starting: "Getting Otto…",
    consentNeeded: "Please check the box to continue.",
    closed: "Otto isn't taking consults right now. Please check back soon.",
    loading: "One moment…",
    newConsult: "Start a new consult",
  },
  es: {
    title: (name: string) => `Qué gusto verle de nuevo, ${name}.`,
    nothing: "No hay una consulta para retomar en este navegador. Puede empezar una nueva cuando quiera.",
    body: (business: string) => `Otto guardó su lugar. Retome la consulta sobre ${business} donde la dejó; solo faltan unos minutos.`,
    start: "Seguir donde nos quedamos",
    starting: "Llamando a Otto…",
    consentNeeded: "Marque la casilla para continuar.",
    closed: "Otto no está tomando consultas en este momento. Vuelva a intentarlo pronto.",
    loading: "Un momento…",
    newConsult: "Empezar una consulta nueva",
  },
} as const;

type Info = { first_name: string; business_name: string; language: "en" | "es"; mode: "video" | "voice" };
type State = { kind: "loading" } | { kind: "ready"; info: Info; config: CheckinConfig } | { kind: "error"; message: string };

function tokenFromHash(): string {
  try {
    return decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
  } catch {
    return "";
  }
}

export function ResumePage() {
  const navigate = useNavigate();
  const [token] = useState(tokenFromHash);
  const [state, setState] = useState<State>({ kind: "loading" });
  const [agreed, setAgreed] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const config = await getCheckinConfig();
      if (cancelled) return;
      if (!config.ok) return setState({ kind: "error", message: config.message });
      if (token) {
        const info = await postResumeInfo(token);
        if (cancelled) return;
        if (!info.ok) return setState({ kind: "error", message: info.message });
        return setState({ kind: "ready", info: info.data, config: config.data });
      }
      const here = await getResumable();
      if (cancelled) return;
      if (!here.ok) return setState({ kind: "error", message: here.message });
      if (!here.data.resumable) return setState({ kind: "error", message: WORDS.en.nothing });
      setState({ kind: "ready", info: here.data, config: config.data });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const language = state.kind === "ready" ? state.info.language : "en";
  const s = stringsFor(language);
  const w = WORDS[language];
  const consent = useMemo(() => {
    if (state.kind !== "ready") return null;
    const notice = state.config.consent_notice;
    if (!notice) return null;
    return { text: stripDashes(notice.text[language] ?? notice.text.en), version: notice.version };
  }, [state, language]);

  const start = async () => {
    if (state.kind !== "ready" || !consent) return;
    if (!agreed) return setError(w.consentNeeded);
    setError("");
    setStarting(true);
    // Inside the tap, before any await: unlock audio for iOS, then the mic (as check-in does).
    primeCallAudio();
    const mic = await requestMicrophone();
    if (!mic.granted) {
      releasePrimedCallAudio();
      setStarting(false);
      return setError(micProblemText(s, mic.problem));
    }
    const consentBody = { agreed: true as const, notice_version: consent.version };
    const result = token ? await postResume({ token, mode: state.info.mode, consent: consentBody }) : await postResumeHere({ mode: state.info.mode, consent: consentBody });
    if (!result.ok) {
      releasePrimedCallAudio();
      releaseMicrophone();
      setStarting(false);
      return setError(result.message);
    }
    const checkin = result.data;
    saveConsultRecord({
      consult_id: checkin.consult_id,
      live_path: checkin.live_path,
      live_token: checkin.live_token,
      live_token_expires_at: checkin.live_token_expires_at,
      mode: checkin.mode,
      language: checkin.language,
      first_name: state.info.first_name.slice(0, 100),
      business_name: state.info.business_name.slice(0, 200),
      agenda: checkin.agenda,
      target_minutes: checkin.target_minutes,
      cap_minutes: checkin.cap_minutes,
      live_origin: checkin.live_origin,
      client_audio: checkin.client_audio,
    });
    // The link has done its job: take the token out of the address bar before leaving.
    try {
      window.history.replaceState(null, "", "/resume");
    } catch {
      // not fatal
    }
    navigate(`/call/${checkin.consult_id}`, { state: { fromCheckin: true, checkin, firstName: state.info.first_name } });
  };

  const closed = state.kind === "ready" && (!state.config.open || !consent);

  return (
    <div className="consult-root oo-consult-root oo-consult-root--complete" lang={language}>
      <PublicHeader s={s} />
      <ConsultBanner label={s.banner.free}>{s.banner.saved}</ConsultBanner>
      <main className="oo-thank-you">
        <section className="oo-thank-you__hero">
          <div className="oo-thank-you__layout">
            <figure className="oo-thank-you__photo">
              <img src="/images/consult/otto-consult-call.jpg" alt={s.thanks.photoAlt} />
            </figure>
            <div className="oo-thank-you__body">
              <img className="oo-thank-you__burst" src="/images/consult/starburst-yellow.png" alt="" />
              {state.kind === "loading" && <p className="oo-thank-you__copy">{w.loading}</p>}
              {state.kind === "error" && (
                <>
                  <p className="oo-thank-you__copy" role="alert">
                    {stripDashes(state.message)}
                  </p>
                  <a className="oo-thank-you__button" href="/">
                    {w.newConsult}
                  </a>
                </>
              )}
              {state.kind === "ready" && (
                <>
                  <h1>{w.title(state.info.first_name)}</h1>
                  <p className="oo-thank-you__copy">{w.body(state.info.business_name)}</p>
                  {closed ? (
                    <p className="oo-thank-you__copy" role="status">
                      {w.closed}
                    </p>
                  ) : (
                    <>
                      <label className="oo-consent">
                        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required aria-describedby="oo-resume-consent" />
                        <span>
                          <b>{s.checkin.consentLabel}</b> <span id="oo-resume-consent">{consent?.text}</span>
                        </span>
                      </label>
                      {error && (
                        <p className="oo-rating__error" role="alert">
                          {error}
                        </p>
                      )}
                      <button type="button" className="oo-thank-you__button" onClick={() => void start()} disabled={starting}>
                        {starting ? w.starting : w.start}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <PublicFooter s={s} />
    </div>
  );
}
